import axios, { AxiosRequestConfig } from 'axios';
import bcrypt from 'bcryptjs-react';
// config
import { HOST_API, NETSUITE_API } from 'src/config-global';

// ----------------------------------------------------------------------

export interface PosApiError extends Error {
  code?: string;
  status?: number;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  data?: any;
  [key: string]: any;
}

export const normalizeApiError = (error: any): PosApiError => {
  const responseData = error?.response?.data;
  const payload = responseData && typeof responseData === 'object' ? responseData : undefined;
  const responseMessage =
    typeof responseData === 'string'
      ? responseData
      : payload?.message || payload?.error || error?.message || 'Something went wrong';

  const normalized = new Error(String(responseMessage)) as PosApiError;
  normalized.name = 'PosApiError';
  normalized.code = error?.code || payload?.code;
  normalized.status = error?.response?.status;
  normalized.isTimeout =
    normalized.code === 'ECONNABORTED' ||
    normalized.code === 'ETIMEDOUT' ||
    /timeout/i.test(normalized.message);
  normalized.isNetworkError =
    !error?.response ||
    ['ERR_NETWORK', 'ECONNRESET', 'ERR_CONNECTION_RESET'].includes(String(normalized.code || '')) ||
    normalized.isTimeout ||
    /network error|failed to fetch|load failed|connection reset|err_connection_reset|econnreset/i.test(normalized.message);
  normalized.data = responseData;

  if (payload) {
    for (const [key, value] of Object.entries(payload)) {
      if (!(key in normalized) || key === 'error' || key === 'action' || key === 'details') {
        (normalized as any)[key] = value;
      }
    }
  }

  return normalized;
};

export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  if (error.isNetworkError === true) return true;

  const code = String(error.code || '');
  const message = String(error.message || error.error || error || '');
  return (
    ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ERR_CONNECTION_RESET'].includes(code) ||
    /network error|failed to fetch|load failed|timeout|connection reset|err_connection_reset|econnreset/i.test(message) ||
    (typeof navigator !== 'undefined' && !navigator.onLine)
  );
};

const salt = import.meta.env.VITE_SALT?.split('-').join('$');
const encrypt = (word: string) => {
  const date = new Date();
  const keyDate = `${date.getDate()}${date.getMonth()}${date.getFullYear()}${date.getSeconds()}`;
  return bcrypt.hashSync(word + keyDate, salt);
};
const axiosNewInstance = (baseURL?: string) => {
  const instance = axios.create({
    baseURL,
    timeout: 180000, // 180 seconds timeout for production stability
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const { config } = error

      // Handle invalid cash register token
      if (error.response?.data?.error === 'Cash register not found' ||
          error.response?.data?.message === 'Account ERP not found' ||
          error.response?.data?.details?.includes('No CashRegister')) {
        console.warn('⚠️ No cash register assigned to user - this is expected for admin users or before setup');
        // window.localStorage.removeItem('CASH-REGISTER-TOKEN');
      }

      // Retry logic for network errors
      if (!config?.retry && error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        config.retry = 1;
        console.warn('⚠️ Network timeout, retrying...', config.url);
        return instance(config);
      }

      return Promise.reject(normalizeApiError(error));
    },
  );
  return instance;
};
const axiosInstance = axiosNewInstance(HOST_API);
const serverEndPoints = (id?: string) => ({
  account: {
    verify: `/account/verify/${id}`,
  },
  init: {
    root: `/v1/init/${id}`,
    stream: `/v1/init/${id}/stream`,
  },
  stripe: {
    readers: `/stripe/${id}/readers`,
    payment_intent: `/stripe/${id}/create-payment-intent`,
    cancelAction: `/stripe/${id}/cancel-action`,
    processPayment: `/stripe/${id}/process-payment`,
    simulatePayment: (paymentIntentId: string) =>
      `/stripe/${id}/simulate-payment/${paymentIntentId}`,
    capturePayment: (paymentIntentId: string) =>
      `/stripe/${id}/capture-payment-intent/${paymentIntentId}`,
    confirmPayment: (paymentIntentId: string) =>
      `/stripe/${id}/confirm-payment-intent/${paymentIntentId}`,
  },
  user: {
    root: `/v1/admin/${id}/user`,
    cognito: `/v1/admin/user/cognito`,
    list: `/v1/user/${id}/user/list`,
    profile: `/v1/user/${id}/profile`,
    accesses: `/v1/user/${id}/accesses`,
    roles: `/v1/user/${id}/roles`,
    cashRegister: `/v1/user/${id}/cashRegister`,
    supply: `/v1/user/${id}/supply`,
    config: `/v1/user/${id}/config`,
    configPlugins: (configId: string) => `/v1/admin/${id}/config/${configId}/plugins`,
  },
  product: {
    list: `/v1/product/${id}/list`,
    sync: `/v1/product/${id}/sync`,
    page: `/products/${id}/page`,
    ids: `/products/${id}/ids`,
    netsuite: `/products/${id}`,
    stockExport: `/v1/product/${id}/stock-export`,
  },
  invoice: {
    root: `/invoice/${id}`,
    page: `/invoice/${id}/page`,
    ids: `/invoice/${id}/ids`,
  },
  return: {
    root: `/return/${id}`,
  },
  customer: {
    root: `/v1/customers/${id}`,
    page: `/v1/customers/${id}/page`,
    ids: `/v1/customers/${id}/ids`,
  },
  customers: {
    root: `/v1/customers/${id}`,
    page: `/v1/customers/${id}/page`,
    ids: `/v1/customers/${id}/ids`,
  },
  cashier: {
    transactions: `/v1/cashier/${id}/transactions`,
    cashRegister: `/v1/cash-registers/${id}`,
    cashTransactions: `/v1/transactions/${id}`,
    customers: `/v1/customers/${id}`,
    inventory: `/v1/inventory/${id}`,
    reporting: `/v1/reporting/${id}`,
    return: `/v1/cashier/${id}/return`,
  },
  reporting: {
    root: `/v1/reporting/${id}`,
  },
  dashboard: {
    root: `/v1/dashboard/${id}`,
    stats: `/v1/dashboard/${id}/stats`,
  },
  notification: {
    root: `/v1/notifications/${id}`,
  },
  voucher: {
    root: `/voucher/${id}`,
  },
});
export type NetsuiteInstanceProps = {
  method: 'post' | 'get' | 'patch' | 'delete'
  idUser?: string
  firstEndPoint?: 'account' | 'user' | 'product' | 'invoice' | 'return' | 'customer' | 'stripe' | 'cashier' | 'customers' | 'inventory' | 'reporting' | 'voucher' | 'dashboard' | 'init' | 'notification'
  lastEndPoint: 'verify' | 'root' | 'cognito' | 'list' | 'page' | 'ids' | 'profile' | 'sync' | 'netsuite' |
    'accesses' | 'roles' | 'payment_intent' | 'readers' | 'cancelAction' | 'processPayment' | 'return' |
    'capturePayment' | 'confirmPayment' | 'simulatePayment' | 'cashRegister' | 'supply' | 'config' |
    'transactions' | 'stockExport' | 'pluginsCatalog' | 'configPlugins' | 'cashTransactions' | 'customers' | 'inventory' | 'reporting' | 'stats' | 'stream'
  endUrl?: string
  data?: any
  endpointParam?: string | Object
  headers?: Object
  responseType?: any
}

/**
 * Initializes a parameter object for API requests.
 * Mutates the target param object with values from the props.
 * This is a shared utility to avoid code duplication across API modules.
 */
export const initRequestParam = (
  target: Partial<NetsuiteInstanceProps>,
  props: NetsuiteInstanceProps
): void => {
  target.data = props.data;
  target.endUrl = props.endUrl || '';
  target.idUser = props.idUser;
  target.lastEndPoint = props.lastEndPoint;
  target.method = props.method;
  target.headers = props.headers ?? {};
  if (props.firstEndPoint) target.firstEndPoint = props.firstEndPoint;
  if (props.endpointParam) target.endpointParam = props.endpointParam;
};
export const axiosServerData = async (props: NetsuiteInstanceProps) => {
  const {
    method, idUser, firstEndPoint, lastEndPoint,
    endUrl, data, endpointParam, headers: oldHeaders, responseType
  } = props;
  const tokenEncrypted = encrypt(process.env.REACT_APP_TOKEN_BAE || '');
  // @ts-ignore
  const endpoint = serverEndPoints(idUser)[firstEndPoint][lastEndPoint]
  const finalEndPoint = typeof endpoint === 'function' ? endpoint(endpointParam) : endpoint
  const headers = {
    ...oldHeaders || {}
    , 'x-pos360-token': tokenEncrypted
    , 'CASH-REGISTER-TOKEN': window.localStorage.getItem('CASH-REGISTER-TOKEN')
  };
  const instance = axiosNewInstance(NETSUITE_API.host);
  const url = `${finalEndPoint}${endUrl}`;
  const config: any = { headers };
  if (responseType) config.responseType = responseType;

  if (method === 'get') {
    return (await instance.get(url, config))?.data;
  }

  if (method === 'delete') {
    return (await instance.delete(url, config))?.data;
  }

  return (await instance[method](url, data, config))?.data;
};

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/auth/me',
    login: '/api/auth/login',
    register: '/api/auth/register',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
};

export default axios;
